import { InjectionToken, ProviderToken, Signal } from '@angular/core';
import { Observable } from 'rxjs';

/** The columns every shooting catalogue row shares (weapon, scope, bullet). */
export interface CatalogueItem {
  id: number;
  title: string;
  description: string | null;
  manufacturer_id: number | null;
  image_url: string | null;
}

export interface CatalogueImageUploadResponse {
  logoUrl: string;
}

/** What the generic catalogue pages need from a catalogue's store service. */
export interface CatalogueStore<T extends CatalogueItem, TCreate = unknown, TUpdate = unknown> {
  items: Signal<T[]>;
  loading: Signal<boolean>;
  error: Signal<unknown>;
  byId: Signal<Map<number, T>>;
  reload: () => void;
  // Method shorthand keeps these bivariant, so a store typed with its own create/update
  // payloads still fits a CatalogueConfig<T>['storeToken'] without a cast.
  create(data: TCreate): Observable<T>;
  update(id: number, data: TUpdate): Observable<T>;
  remove: (id: number) => Observable<void>;
  uploadImage: (file: File) => Observable<CatalogueImageUploadResponse>;
}

/** One catalogue's identity for the generic list, detail and create/edit pages. */
export interface CatalogueConfig<T extends CatalogueItem> {
  /** Singular noun, capitalised: 'Weapon'. Used in headings, toasts and messages. */
  label: string;
  /** Plural noun, capitalised: 'Weapons'. */
  plural: string;
  /** Route base without trailing slash: '/weapons'. */
  routeBase: string;
  /** The store service class the pages inject. */
  storeToken: ProviderToken<CatalogueStore<T>>;
  /** Taiga icon shown when a row has no image. */
  placeholderIcon: string;
  /** Short badge texts for a row, e.g. calibre; empty strings are skipped. */
  badges: (item: T) => string[];
}

/** Provided per catalogue on its route (`providers: [{ provide: CATALOGUE_CONFIG, useValue: ... }]`). */
export const CATALOGUE_CONFIG = new InjectionToken<CatalogueConfig<CatalogueItem>>('CATALOGUE_CONFIG');
